import {useCallback, useEffect, useRef, useState} from 'react'

/** `en` usa el reloj del navegador a propósito: el borrador nunca llega al servidor ni decide nada de negocio. */
type Borrador = {respuestas: Record<string, string | null>; en: number}

const PREFIJO = 'digitacion:'

function clave(paqueteId: string): string {
  return `${PREFIJO}${paqueteId}`
}

function leer(paqueteId: string): Borrador | null {
  try {
    const crudo = window.localStorage.getItem(clave(paqueteId))
    if (crudo === null) return null
    const dato = JSON.parse(crudo) as Borrador
    // Un borrador de otra versión o corrupto se ignora en vez de romper la pantalla.
    return dato && typeof dato === 'object' && dato.respuestas ? dato : null
  } catch {
    return null
  }
}

/** Re-entrar es seguro porque el POST de digitación es idempotente; se descarta al guardar con éxito. */
export function useBorradorDigitacion(paqueteId: string) {
  const [guardado] = useState<Borrador | null>(() => leer(paqueteId))
  const ultimas = useRef<Record<string, string | null>>({})

  const escribir = useCallback(
    (respuestas: Record<string, string | null>) => {
      ultimas.current = respuestas
      const contestadas = Object.values(respuestas).filter((v) => v != null && v !== '').length
      if (contestadas === 0) return
      const dato: Borrador = {respuestas, en: Date.now()}
      window.localStorage.setItem(clave(paqueteId), JSON.stringify(dato))
    },
    [paqueteId]
  )

  const descartar = useCallback(() => {
    window.localStorage.removeItem(clave(paqueteId))
  }, [paqueteId])

  // El navegador decide el texto del aviso; acá solo se controla cuándo aparece.
  useEffect(() => {
    const alSalir = (evento: BeforeUnloadEvent) => {
      const contestadas = Object.values(ultimas.current).filter((v) => v != null && v !== '').length
      if (contestadas === 0) return
      evento.preventDefault()
    }
    window.addEventListener('beforeunload', alSalir)
    return () => window.removeEventListener('beforeunload', alSalir)
  }, [])

  return {guardado, escribir, descartar}
}
